/* ============================================================
   QUICK ADD SIZES — Collection Card Interaction Logic
   ============================================================
   Sizes are server-rendered in Liquid for instant display.
   JS only handles:
     1. Add-to-cart on size button click
     2. Re-rendering sizes when a color swatch changes the handle
     3. Mobile popup open/close
   ============================================================ */

(function () {
  'use strict';

  if (window.__quickAddSizesInit) return;
  window.__quickAddSizesInit = true;

  var productCache = Object.create(null);

  /* ---------- helpers ---------- */

  function getCard(el) {
    return el.closest('product-card.product-card');
  }

  function getActiveHandle(card) {
    return card.dataset.activeHandle || card.getAttribute('handle') || '';
  }

  function getOriginalHandle(card) {
    var overlay = card.querySelector('[data-quick-add-sizes]');
    return overlay ? overlay.dataset.productHandle : '';
  }

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return fetch(Shopify.routes.root + 'products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (data) productCache[handle] = data;
        return data;
      });
  }

  /* ---------- Render buttons into a container ---------- */

  function renderButtons(container, variants, isSingleVariant) {
    container.innerHTML = '';
    if (!variants || variants.length === 0) {
      container.innerHTML = '<span style="font-size:11px;color:#999;">Unavailable</span>';
      return;
    }
    // Single-variant → render as hyperlink-style Add to Cart, matching the
    // Liquid-rendered markup. Multi-variant → pill size buttons.
    if (isSingleVariant) {
      var v = variants[0];
      var link = document.createElement('button');
      link.type = 'button';
      link.className = 'quick-add-link';
      link.textContent = v.available ? 'Add to Cart' : 'Sold Out';
      link.dataset.variantId = v.id;
      if (!v.available) link.disabled = true;
      container.appendChild(link);
      return;
    }
    variants.forEach(function (v) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'quick-add-sizes__btn' + (v.available ? '' : ' quick-add-sizes__btn--sold-out');
      btn.textContent = v.title;
      btn.dataset.variantId = v.id;
      if (!v.available) btn.disabled = true;
      container.appendChild(btn);
    });
  }

  function getSizeVariants(product) {
    if (!product || !product.variants) return [];
    if (product.variants.length === 1 && product.variants[0].title === 'Default Title') {
      return [{ id: product.variants[0].id, title: 'Add to Cart', available: product.variants[0].available }];
    }
    return product.variants.map(function (v) {
      var label = v.title;
      if (product.options && product.options.length > 1) {
        label = v.option1 || v.title;
      }
      return { id: v.id, title: label, available: v.available };
    });
  }

  function isSingleVariantProduct(product) {
    return product && product.variants && product.variants.length === 1 && product.variants[0].title === 'Default Title';
  }

  /* ---------- Re-render sizes when color swatch changes ---------- */

  function refreshSizes(card, handle) {
    var desktopBtns = card.querySelector('[data-quick-add-buttons]');
    var mobileBtns = card.querySelector('[data-quick-add-mobile-buttons]');

    fetchProduct(handle).then(function (product) {
      // Bail if handle changed again while fetching
      if (getActiveHandle(card) !== handle) return;
      var variants = getSizeVariants(product);
      var isSingle = isSingleVariantProduct(product);
      if (desktopBtns) renderButtons(desktopBtns, variants, isSingle);
      if (mobileBtns) renderButtons(mobileBtns, variants, isSingle);
    });
  }

  /* ---------- Observe color swatch handle changes ---------- */

  var observedCards = new WeakSet();

  var handleObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      if (m.attributeName !== 'data-active-handle') return;
      var card = m.target;
      var newHandle = getActiveHandle(card);
      // If it matches the Liquid-rendered handle, restore original HTML?
      // No — just re-fetch. The HTTP cache will make it instant anyway.
      if (newHandle) refreshSizes(card, newHandle);
    });
  });

  function observeCards() {
    document.querySelectorAll('product-card.product-card').forEach(function (card) {
      if (observedCards.has(card)) return;
      observedCards.add(card);
      handleObserver.observe(card, { attributes: true, attributeFilter: ['data-active-handle'] });
    });
  }

  /* ---------- Add to cart ---------- */

  function addToCart(variantId, btn) {
    var isLink = btn.classList.contains('quick-add-link');
    var originalText = btn.textContent;
    if (isLink) {
      // Hyperlink-style single-variant Add to Cart — preserve its look, just tweak text
      btn.textContent = 'Adding\u2026';
    } else {
      btn.classList.add('quick-add-sizes__btn--adding');
      btn.textContent = '...';
    }

    var sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sections }
    }));

    var formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');
    formData.set('on_success', 'force_open_drawer');
    if (sections.length) {
      formData.set('sections', sections.join(','));
      formData.set('sections_url', Shopify.routes.root + 'variants/' + variantId);
    }

    fetch(Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) { return response.json().then(function (json) { return { ok: response.ok, json: json }; }); })
      .then(function (result) {
        if (!result.ok) {
          btn.classList.remove('quick-add-sizes__btn--adding');
          btn.textContent = originalText;
          if (!isLink) {
            btn.style.borderColor = '#c00';
            setTimeout(function () { btn.style.borderColor = ''; }, 1200);
          }
          return;
        }

        if (isLink) {
          btn.textContent = 'Added';
        } else {
          btn.classList.remove('quick-add-sizes__btn--adding');
          btn.classList.add('quick-add-sizes__btn--added');
          btn.textContent = '';
        }

        fetch(Shopify.routes.root + 'cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cartContent) {
            cartContent.sections = result.json.sections || {};

            document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
              bubbles: true,
              detail: {
                items: result.json.items ? result.json.items : [result.json],
                cart: cartContent,
                onSuccessDo: 'force_open_drawer'
              }
            }));

            document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
              bubbles: true,
              detail: {
                baseEvent: 'variant:add',
                onSuccessDo: 'force_open_drawer',
                cart: cartContent
              }
            }));
          });

        setTimeout(function () {
          if (!isLink) btn.classList.remove('quick-add-sizes__btn--added');
          btn.textContent = originalText;
        }, 1500);
      })
      .catch(function () {
        if (!isLink) btn.classList.remove('quick-add-sizes__btn--adding');
        btn.textContent = originalText;
      });
  }

  /* ---------- Mobile popup open/close ---------- */

  function openMobilePopup(card) {
    var popup = card.querySelector('[data-quick-add-mobile-popup]');
    var backdrop = card.querySelector('[data-quick-add-backdrop]');
    if (!popup) return;

    popup.classList.add('is-visible');
    if (backdrop) backdrop.classList.add('is-active');

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        popup.classList.add('is-active');
      });
    });
  }

  function closeMobilePopup(card) {
    var popup = card.querySelector('[data-quick-add-mobile-popup]');
    var backdrop = card.querySelector('[data-quick-add-backdrop]');
    if (!popup) return;

    popup.classList.remove('is-active');
    if (backdrop) backdrop.classList.remove('is-active');

    setTimeout(function () {
      popup.classList.remove('is-visible');
    }, 200);
  }

  function closeAllMobilePopups() {
    document.querySelectorAll('.quick-add-sizes__mobile-popup.is-visible').forEach(function (popup) {
      var card = getCard(popup);
      if (card) closeMobilePopup(card);
    });
  }

  /* ---------- Event delegation ---------- */

  // Size button click (desktop + mobile) — also matches the single-variant "Add to Cart" hyperlink
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.quick-add-sizes__btn, .quick-add-link');
    if (!btn || btn.disabled) return;

    e.preventDefault();
    e.stopPropagation();

    var variantId = btn.dataset.variantId;
    if (!variantId) return;

    // Keep the card in its hover state while the cart drawer opens.
    // mouseleave fires immediately when the drawer covers the card, so we
    // DON'T use it to clear state. Instead, clear on the next document click
    // outside the card — lets the user actually see the card + drawer together.
    var card = btn.closest('product-card');
    if (card) {
      card.classList.add('is-sticky-open');

      // One-time document click listener, installed after this tick so it
      // doesn't catch the current click event.
      setTimeout(function () {
        function onDocClick(ev) {
          if (!card.contains(ev.target)) {
            card.classList.remove('is-sticky-open');
            document.removeEventListener('click', onDocClick, true);
          }
        }
        document.addEventListener('click', onDocClick, true);
      }, 0);
    }

    addToCart(variantId, btn);
  }, true);

  // Mobile trigger
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-quick-add-mobile-trigger]');
    if (!trigger) return;

    e.preventDefault();
    e.stopPropagation();

    var card = getCard(trigger);
    if (!card) return;

    closeAllMobilePopups();
    openMobilePopup(card);
  });

  // Mobile close
  document.addEventListener('click', function (e) {
    var closeBtn = e.target.closest('[data-quick-add-mobile-close]');
    if (!closeBtn) return;

    e.preventDefault();
    e.stopPropagation();

    var card = getCard(closeBtn);
    if (card) closeMobilePopup(card);
  });

  // Backdrop
  document.addEventListener('click', function (e) {
    if (e.target.matches && e.target.matches('[data-quick-add-backdrop]')) {
      closeAllMobilePopups();
    }
  });

  // Close on scroll
  var scrollTimer;
  window.addEventListener('scroll', function () {
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(closeAllMobilePopups, 100);
  }, { passive: true });

  /* ---------- Init ---------- */

  observeCards();
  document.addEventListener('shopify:section:load', observeCards);

  var bodyObserver = new MutationObserver(observeCards);
  bodyObserver.observe(document.body, { childList: true, subtree: true });

})();

